import { type Decorator, type FormApi, getIn } from '@atlassian/eoc-form';

type FormInput = {
	id?: string;
	name: string;
	focus: (options: { preventScroll?: boolean; focusVisible?: boolean }) => void;
	type?: string;
} & Nullable<Pick<Element, 'scrollIntoView'>>;

const noop = () => {};

const findInput = (inputs: FormInput[], errors: {}) =>
	inputs.find((input) => input.name && getIn(errors, input.name));

const getAllInputs = () => {
	if (typeof document === 'undefined') {
		return [];
	}
	return Array.prototype.slice
		.call(document.forms)
		.reduce((accumulator, form) => accumulator.concat(Array.prototype.slice.call(form)), []);
};

export const createFormFocusDecorator =
	<T>(sectionMessageRef?: React.MutableRefObject<HTMLElement | null>): Decorator<T> =>
	(form: FormApi<T>) => {
		// Save original submit function
		const originalSubmit = form.submit;
		let state: { errors?: Object; submitErrors?: Object } = {};

		const focusOnFirstError = (errors: Object) => {
			const firstInput = findInput(getAllInputs(), errors);
			if (firstInput) {
				// Finding by id is the most precise method, but it is unavailable for select and tag-input
				let targetLabelForAttribute: string;

				if (!!firstInput.id) {
					targetLabelForAttribute = firstInput.id;
				} else {
					targetLabelForAttribute = firstInput.name;
				}

				const targetLabel = document.querySelector(`label[for^="${targetLabelForAttribute}"]`);

				// Input doesn't have label
				if (!targetLabel) {
					firstInput?.scrollIntoView({
						behavior: 'smooth',
						block: 'center',
					});
				} else {
					targetLabel?.scrollIntoView({
						behavior: 'smooth',
						block: 'center',
					});
				}

				firstInput.focus({ preventScroll: true });
			} else {
				if (errors) {
					const firstError: string | undefined = Object.keys(errors)?.[0];
					if (firstError) {
						// Trying label
						const targetLabel = document.querySelector(`label[for^="${firstError}"]`);

						if (targetLabel) {
							targetLabel?.scrollIntoView({
								behavior: 'smooth',
								block: 'center',
							});

							return;
						}

						// Trying error message
						const errorMessage = document.querySelector(
							`#${firstError}-error`, // Came from Atlaskit Error Message fieldId prop
						);

						if (errorMessage) {
							errorMessage?.scrollIntoView({
								behavior: 'smooth',
								block: 'center',
							});

							return;
						}
					}
				}

				// The error is not in an input, it is in a section message
				sectionMessageRef?.current?.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
				});
			}
		};

		// What to do after submit
		const afterSubmit = () => {
			const { errors, submitErrors } = state;
			if (errors && Object.keys(errors).length) {
				focusOnFirstError(errors);
			} else if (submitErrors && Object.keys(submitErrors).length) {
				focusOnFirstError(submitErrors);
			}
		};

		// Overwrite submit function
		form.submit = () => {
			const result = originalSubmit.call(form);
			if (result && typeof result.then === 'function') {
				// async
				result.then(afterSubmit, noop);
			} else {
				// sync
				afterSubmit();
			}
			return result;
		};

		const unsubscribe = form.subscribe(
			(nextState) => {
				state = nextState;
			},
			{ errors: true, submitErrors: true },
		);

		return () => {
			unsubscribe();
			form.submit = originalSubmit;
		};
	};
